<template>
  <div class="store-table__layout scroll-viewport"
  ref="storeTableLayout"
  @mousedown="onMouseDown"
  @mousemove="onMouseMove"
  @mouseup="onMouseUp"
  @mouseleave="onMouseUp">
    <div class="scroll-arrows">
      <div 
      class="scroll-arrow-left scroll-left"
      @click="scrollLeft"
      ></div>
      <div 
      class="scroll-arrow-right scroll-right" 
      @click="scrollRight"
      ></div>
    </div>

    <div
      class="store-table scroll-content"
      v-bind:class="{
      'store-table--discount': showDiscountLabel
      }"
    >
      <table>
        <thead>
          <tr>
            <th class="store-table__image" v-if="!hideImage"></th>

            <th class="store-table__cart store-table__name">
              <template v-if="!isRentCategory()">{{ translations.addToCart }}</template>
            </th>

            <th
              class="store-table__price"
              v-bind:class="{
                'store-table__price--discount': showDiscountLabel,
              }" 
              v-tax-toggle="toggleTax"
            >
            <span v-if="isRentCategory()" class="store-table__price__default-label">
              {{ translations.productPrice }} / {{ translations.rentDay }} {{ getVatStatusTranslation() }}
            </span>
            <template v-else-if="showDiscountLabel">
              <template v-if="isLoggedIn">
                <span class="store-table__price__resale-label">
                  {{ translations.productResale }} {{ getVatStatusTranslation() }}
                </span>
                <span class="store-table__price__default-label">
                  {{ translations.productPrice }} {{ getVatStatusTranslation() }}
                </span>
              </template>
              <template v-else="">
                <span class="store-table__price__resale-label">
                  {{ translations.productPrice }} {{ getVatStatusTranslation() }}
                </span>
                <span class="store-table__price__default-label">
                  {{ translations.productDiscount }} {{ getVatStatusTranslation() }}
                </span>
              </template>
            </template>
            <template v-else="">
              <span class="store-table__price__default-label">
                {{ translations.productPrice }} {{ getVatStatusTranslation() }}
              </span>
            </template>
            </th>

            <th
              class="store-table__key"
              v-for="key in displayedProductsAttributes"
              :key="key"
              :data-column="key"
            >
              {{ getAttributeTaxonomyLabel(key) }}
            </th>

            <th class="store-table__other" v-if="showOtherInfo">
              {{ translations.other }}
            </th>
          </tr>
        </thead>

        <template v-for="product in displayedProducts">
          <VariationRow
            :product="product"
            :displayedAttributeTaxonomies="displayedProductsAttributes"
            :showDiscountLabel="showDiscountLabel"
            v-if="product.is_variable"
            :key="`${prefix}${product.id}`"
            :showOtherInfo="showOtherInfo"
            :hideImage="hideImage"
            :hideName="hideName"
            :hideSKU="hideSKU"
          />
          <SimpleRow
            :product="product"
            :displayedAttributeTaxonomies="displayedProductsAttributes"
            :showDiscountLabel="showDiscountLabel"
            v-if="!product.is_variable"
            :key="`${prefix}${product.id}`"
            :showOtherInfo="showOtherInfo"
            :hideImage="hideImage"
            :hideName="hideName"
            :hideSKU="hideSKU"
          />
        </template>

      </table>
    </div>
  </div>
</template>

<script>
import VariationRow from './VariationRow.vue';
import SimpleRow from './SimpleRow.vue';
import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';

export default {
  name: 'ProductTable',
  components: {
    VariationRow,
    SimpleRow,
  },
  props: {
    prefix: {
      type: String,
      default() {
        return '';
      },
    },
    displayedProducts: {
      type: Array,
      default() {
        return [];
      },
    },
    hideImage: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideName: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideSKU: {
      type: Boolean,
      default() {
        return ws_vue_store.archive_product_sku === '0';
      },
    },
    rentCategory: {
      type: Boolean,
      default() {
        return (this.$store.getters.getRentCategory === '1');
      },
    },
  },
  data() {
    return {
      translations: {
        productName: ws_vue_store.translations.product_name,
        productSKU: ws_vue_store.translations.product_SKU,
        productPrice: ws_vue_store.translations.product_price,
        productResale: ws_vue_store.translations.product_resale,
        productDiscount: ws_vue_store.translations.product_discount,
        other: ws_vue_store.translations.other,
        withVat: ws_vue_store.translations.excluded_vat,
        withoutVat: ws_vue_store.translations.included_vat,
        addToCart: ws_vue_store.translations.add_to_cart_heading,
        rentDay: ws_vue_store.translations.day,
        noProductsMatch: ws_vue_store.translations.no_products_match
      },
      includingTax: this.getTaxCookie(),
      isLoggedIn: Boolean(ws_vue_store.logged_in),
      allowedAttributes: this.$store.getters.getAllowedAttributes,
      isDragging: false,
      startX: 0,
      scrollTableLeft: 0,
      showScrollButton: false,
      psInstance: null,
    };
  }, // data
  created() {}, // created
  mounted() {
    // Initialize PerfectScrollbar
    this.psInstance = new PerfectScrollbar(this.$refs.storeTableLayout, {
      wheelSpeed: 1,
      suppressScrollX: false,
      swipeEasing: true,
    });

    // Add event listeners for mouse and touch events
    const tableLayout = this.$refs.storeTableLayout;

    // Mouse Events
    tableLayout.addEventListener('mousedown', this.onMouseDown);
    tableLayout.addEventListener('mousemove', this.onMouseMove);
    tableLayout.addEventListener('mouseup', this.onMouseUp);
    tableLayout.addEventListener('mouseleave', this.onMouseUp);

    // Touch Events (for mobile)
    tableLayout.addEventListener('touchstart', this.onTouchStart);
    tableLayout.addEventListener('touchmove', this.onTouchMove);
    tableLayout.addEventListener('touchend', this.onTouchEnd);

    // Listen for window resize events to adjust the scrollbar
    window.addEventListener('resize', this.handleResize);

    // ResizeObserver to track size changes of the table container
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.$refs.storeTableLayout);
  },

  beforeDestroy() {
    if (this.psInstance) {
      this.psInstance.destroy();
      this.psInstance = null;
    }

    // Remove event listeners for mouse and touch events
    const tableLayout = this.$refs.storeTableLayout;
    
    // Mouse Events
    tableLayout.removeEventListener('mousedown', this.onMouseDown);
    tableLayout.removeEventListener('mousemove', this.onMouseMove);
    tableLayout.removeEventListener('mouseup', this.onMouseUp);
    tableLayout.removeEventListener('mouseleave', this.onMouseUp);

    // Touch Events
    tableLayout.removeEventListener('touchstart', this.onTouchStart);
    tableLayout.removeEventListener('touchmove', this.onTouchMove);
    tableLayout.removeEventListener('touchend', this.onTouchEnd);

    // Remove the resize event listener
    window.removeEventListener('resize', this.handleResize);

    // Stop observing the resize of the container
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  directives: {
    "tax-toggle": {
      bind(el, binding) {
        window.wsTaxesToggledDirectiveEvent = event => binding.value(event);
        document.addEventListener("taxes-toggled", window.wsTaxesToggledDirectiveEvent);
      },
      unbind() {
        document.removeEventListener("taxes-toggled", window.wsTaxesToggledDirectiveEvent);
      }
    }
  },
  computed: {
    gridColumnTemplate() {
      let columnRule = '8rem 38rem';
      let NrOfColumns = this.displayedProductsAttributes.length;

      if (this.showDiscountLabel) {
        columnRule += ' 1fr';
      } else {
        columnRule += ' 1fr';
      }

      for (let i = NrOfColumns; i > 0; i--) {
        columnRule += ' 1fr';
      }

      return {
        'grid-template-columns': columnRule
      };
    },
    showDiscountLabel() {
      for (const product of this.displayedProducts) {
        if (product.sale_price) return true;
        if (product.is_variable) {
          for (const variation of product.variations) {
            if (variation.sale_price) return true;
          }
        }
      }
      return false;
    },
    isDiscountLabel() {
      return ws_vue_store.logged_in === '';
    },
    showPackageColumn() {
      for (const product of this.displayedProducts) {
        try {
          let data = JSON.parse(product.packages);
          if (data.length && data.length > 1) {
            return true;
          }
        } catch {}
        for (const variation of product.variations) {
          try {
            let data = JSON.parse(variation.packages);
            if (data.length && data.length > 1) {
              return true;
            }
          } catch {}
        }
      }

      return false;
    },
    displayedProductsAttributes() {
      if (this.displayedProducts.length === 0 || this.displayedProducts == undefined) {
        return [];
      }

      let order = this.allowedAttributes;

      // Use Set() for it's unique value property
      let currentAttributes = new Set();
      // Get category attributes that are defined
      // on product in current search result.
      for (const product of this.displayedProducts) {
        for (const attr of product.attributes) {
          if (this.allowedAttributes.includes(attr.key) == false) {
            order.push(attr.key);
          }
          currentAttributes.add(attr.key);
        }
      }

      let hasBrand = currentAttributes.delete('pa_brand');

      // Convert Set() to array and
      // sort attributes to correct order
      currentAttributes = [
        ...currentAttributes
      ].sort((a, b) => {
        return this.allowedAttributes.indexOf(a) - this.allowedAttributes.indexOf(b);
      });

      if (this.showPackageColumn) {
        currentAttributes.unshift('package_sizes');
      }

      // Move 'pa_brand' column to the end and add non attribute "sku" column
      if (hasBrand) {
        currentAttributes.push(this.translations.productSKU);
        currentAttributes.push('pa_brand');
      } else {
        currentAttributes.push(this.translations.productSKU);
      }

      return currentAttributes;
    },
    showOtherInfo() {
      if (this.displayedProducts.length === 0) {
        return false;
      }

      return this.displayedProducts.some(product => 
        !product.is_variable
          ? product.excerpt.length
          : product.excerpt.length
      );
    },
  }, // computed
  watch: {}, // watch
  methods: {
    handleResize() {
      // Update the PerfectScrollbar instance to account for layout changes
      if (this.psInstance) {
        this.psInstance.update();
      }
    },
    scrollLeft() {
      const container = this.$refs.storeTableLayout;
      if (container) {
        container.scrollLeft -= 100;
        this.scrollSmoothly(container, -100);
      }
    },
    scrollRight() {
      const container = this.$refs.storeTableLayout;
      if (container) {
        container.scrollLeft += 100;
        this.scrollSmoothly(container, 100);
      }
    },
    onMouseDown(event) {
      if (event.target.closest('.scroll-arrow-left') || event.target.closest('.scroll-arrow-right')) {
        return;
      }

      const table = event.currentTarget;
      this.isDragging = true;
      this.startX = event.pageX - table.offsetLeft;
      this.scrollLeft = table.scrollLeft;
      table.classList.add('dragging');
    },
    onMouseMove(event) {
      if (!this.isDragging) return;
      const table = event.currentTarget;
      const x = event.pageX - table.offsetLeft;
      const walk = (x - this.startX) * 2;
      table.scrollLeft = this.scrollLeft - walk;
    },
    onMouseUp(event) {
      const table = event.currentTarget;
      this.isDragging = false;
      table.classList.remove('dragging');
    },
    onTouchStart(event) {
      if (event.target.closest('.scroll-arrow-left') || event.target.closest('.scroll-arrow-right')) {
        return;
      }

      const table = event.currentTarget;
      this.isDragging = true;
      this.startX = event.touches[0].pageX - table.offsetLeft;
      this.scrollLeft = table.scrollLeft;
      table.classList.add('dragging');
    },

    // Handle Touch Move event (for mobile)
    onTouchMove(event) {
      if (!this.isDragging) return;
      const table = event.currentTarget;
      const x = event.touches[0].pageX - table.offsetLeft;
      const walk = (x - this.startX) * 2;
      table.scrollLeft = this.scrollLeft - walk;
    },

    // Handle Touch End event (for mobile)
    onTouchEnd(event) {
      const table = event.currentTarget;
      this.isDragging = false;
      table.classList.remove('dragging');
    },
    getProductPackages() {
      if (this.variation.packages) {
        try {
          return JSON.parse(this.variation.packages);
        } catch {}
      }
      return [];
    },
    getSelectedPackage() {
      if ( ! this.packages.length) return '';

      let selectedPackages = this.$store.getters.getSelectedPackages;
      return selectedPackages[this.variation.id] ? selectedPackages[this.variation.id] : '';
    },
    getAttributeTaxonomyLabel(taxonomyName) {
      const taxonomy = this.$store.getters.getAttributeTaxonomy(taxonomyName);
      if (taxonomy) {
        return taxonomy.label;
      } else if (taxonomyName == this.translations.productSKU) {
        return this.translations.productSKU;
      } else if (taxonomyName == 'package_sizes') {
        return 'Pakendi suurused';
      }
      return '';
    },
    isRentCategory()
    {
      return  this.$store.getters.getRentCategory==='1';
    },
    getVatStatusTranslation() {
      if (this.includingTax) {
        return this.translations.withVat;
      } else {
        return this.translations.withoutVat;
      }
    },
    getTaxCookie() {
      let name = 'ws_taxes_display_selection';
    	let cookie = document.cookie.match(new RegExp(
        "(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"
      ));

      return (cookie) ? cookie[1] === 'taxes_excluded' : true;
    },
    toggleTax() {
      this.includingTax = !this.includingTax;
    },
    hasBrand() {
      return this.displayedProductsAttributes.includes('pa_brand');
    },
  }, // methods
};
</script>

<style scoped lang="scss">
.store-table {
  &__head {
    display: grid;
    // grid-template-columns: 6rem 20rem 0.2fr 0.1fr 0.1fr 0.1fr 0.1fr 0.4fr 12rem;
    // gap: 2rem;

    div {
      //word-break: break-all;
    }
  }
}
</style>
